import ButtonDefault from 'ui/src/buttons/ButtonDefault'
import styles from 'ui/src/styles/components/parts/Input.module.scss'
import { useAuth } from '@/components/providers/AuthProvider'
import { useEffect, useState } from 'react'
import useSWR from 'swr'
import { apiUrls } from '@/libs/apiUrls'
import { ResultMsg } from '@/libs/types/ResultMsg'
import { TPersonInfo } from '@/libs/types/TPersonInfo'
import { TJobInfo } from '@/libs/types/TJobInfo'
import { useLocale } from '@/features/i18n/hooks/useLocale'

interface Props {
  className?: string
  isOpen?: boolean
  onClose?: () => void
}

interface Inputs {
  current: string
  new: string
  confirm: string
}

interface Errors {
  current?: string
  new?: string
  confirm?: string
}

export default function TableModalEmailEdit({ className, isOpen, onClose }: Props) {
  const { t } = useLocale()
  const { token, tUser } = useAuth()

  const [inputs, setInputs] = useState<Inputs>({
    current: '',
    new: '',
    confirm: '',
  })
  const [errors, setErrors] = useState<Errors>({})

  const { data: userData } = useSWR(
    token ? [apiUrls.personal.getPersonInfo(), token, tUser?.id] : null,
    async ([url, token, userId]) => {
      if (!token || !userId) {
        return
      }
      const urlParams = new URLSearchParams({ personId: userId, token })
      const response = await fetch(`${url}?${urlParams.toString()}`, {
        method: 'GET',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json;charset=utf-8' },
      })
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      const result = (await response.json()) as ResultMsg<{
        personInfo: TPersonInfo
        jobInfoList: TJobInfo[]
        existingMail: string
      }>
      // console.log({ result })
      return result.extend
    },
  )

  useEffect(() => {
    // モーダルを表示したときに入力欄とエラー表示をリセット
    if (isOpen) {
      setInputs({ current: '', new: '', confirm: '' })
      setErrors({ current: undefined, new: undefined, confirm: undefined })
    }
  }, [isOpen])
  useEffect(() => {
    // モーダルを表示したときに現在のメールアドレス欄にAPIから取得した値を入れる
    if (isOpen && userData) {
      setInputs((inputs) => ({ ...inputs, current: userData.existingMail }))
    }
  }, [isOpen, userData])

  const checkConfirmInput = () => {
    if (inputs.new && inputs.confirm && inputs.new !== inputs.confirm) {
      setErrors((errors) => ({ ...errors, confirm: t.validate.equalTo }))
    } else {
      setErrors((errors) => ({ ...errors, confirm: '' }))
    }
  }
  const saveAction = async () => {
    let newErrors: Errors = {}
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    if (!inputs.current) {
      newErrors.current = t.validate.required
    } else if (!emailRegex.test(inputs.current)) {
      newErrors.current = t.validate.invalidEmail // 新しいエラーメッセージを追加してください
    }
    if (
      inputs.current &&
      inputs.new &&
      inputs.confirm &&
      inputs.current !== userData?.personInfo.email
    ) {
      // すべて入力されているときのみチェックする
      newErrors.current = t.validate.equalTo
    }
    if (!inputs.new) {
      newErrors.new = t.validate.required
    } else if (!emailRegex.test(inputs.new)) {
      newErrors.new = t.validate.invalidEmail
    }
    if (!inputs.confirm) {
      newErrors.confirm = t.validate.required
    } else if (!emailRegex.test(inputs.confirm)) {
      newErrors.confirm = t.validate.invalidEmail
    }
    if (
      inputs.current &&
      inputs.new &&
      inputs.confirm &&
      inputs.current !== userData?.personInfo.email
    ) {
      newErrors.current = t.validate.equalTo
    }
    if (inputs.new && inputs.confirm && inputs.new !== inputs.confirm) {
      newErrors.confirm = t.validate.equalTo
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors)
      return
    }
    setErrors({})

    const data = {
      newEmail: inputs.new,
      userId: tUser.id,
      roleId: tUser.roleId,
    }

    try {
      const response = await fetch(apiUrls.user.changeUserEmail(), {
        method: 'post',
        body: JSON.stringify(data),
        headers: { Accept: 'application/json', 'Content-Type': 'application/json;charset=utf-8' },
        cache: 'no-store',
      })
      if (response.ok) {
        const result = await response.json()
        switch (result.status) {
          case 1: // 成功
            onClose?.()
            setErrors({})
            return
          default:
          // console.log(result.msg)
        }
      }
    } catch {}

    setErrors((errors) => ({
      ...errors,
      confirm: t.header.EmailChangeFailed,
    }))
  }

  return (
    <>
      <table className="mb-5">
        <tbody>
          <tr>
            <th className="font-bold">
              <div className="flex gap-1 items-center">
                {t.header.OldEmail}
                <span className={styles.input__must}>{t.validate.requiredLabel}</span>
              </div>
            </th>
          </tr>
          <tr>
            <td>
              <input
                type="text"
                className={`${styles.inputText} ${styles.inputText__l} ${styles.gray}`}
                placeholder={t.header.OldEmailPlaceholder}
                value={inputs.current}
                onChange={(e) => {
                  setInputs((inputs) => ({ ...inputs, current: e.target.value }))
                }}
              />
              <span className={styles.error}>{errors.current}</span>
            </td>
          </tr>
          <tr>
            <th className="font-bold">
              <div className="flex gap-1 items-center">
                {t.header.NewEmail}
                <span className={styles.input__must}>{t.validate.requiredLabel}</span>
              </div>
            </th>
          </tr>
          <tr>
            <td>
              <input
                type="text"
                className={`${styles.inputText} ${styles.inputText__l} ${styles.gray}`}
                placeholder={t.header.NewEmailPlaceholder}
                value={inputs.new}
                onChange={(e) => {
                  setInputs((inputs) => ({ ...inputs, new: e.target.value }))
                }}
                onBlur={checkConfirmInput}
              />
              <span className={styles.error}>{errors.new}</span>
            </td>
          </tr>
          <tr>
            <th className="font-bold">
              <div className="flex gap-1 items-center">
                {t.header.ConfirmEmail}
                <span className={styles.input__must}>{t.validate.requiredLabel}</span>
              </div>
            </th>
          </tr>
          <tr>
            <td>
              <input
                type="text"
                className={`${styles.inputText} ${styles.inputText__l} ${styles.gray}`}
                placeholder={t.header.NewEmailPlaceholder}
                value={inputs.confirm}
                onChange={(e) => {
                  setInputs((inputs) => ({ ...inputs, confirm: e.target.value }))
                }}
                onBlur={checkConfirmInput}
              />
              <span className={styles.error}>{errors.confirm}</span>
            </td>
          </tr>
        </tbody>
      </table>
      <hr className="hr mb-7" />
      <div className="flex gap-5 justify-center">
        <ButtonDefault label={t.button.Cancel} color="secondary" action={onClose} />
        <ButtonDefault label={t.button.Save} color="primary" action={saveAction} />
      </div>
    </>
  )
}
